/**
 * Ministry edition detection — path-prefix based.
 *
 * The MOH white-label tenant lives at n71.ai/mohap (and any other
 * tenant follows the n71.ai/<slug> pattern). The same React bundle
 * serves all tenants; this module decides whether the current load is
 * a white-label edition and which workspace slug to lock in based on
 * the URL path.
 *
 * Why path-prefix instead of subdomain (decision 2026-05-02): keeps
 * DNS, TLS, and CDN config identical across tenants — one origin
 * serves everyone. If a customer later asks for a branded domain
 * (moh.gov / portal.dha.ae / similar) that becomes a deployment-time
 * CNAME + a small middleware tweak (read the host header in addition
 * to the path), not an architecture change.
 *
 * Three detection signals (any one wins, in priority order):
 *   1. Path prefix: /<slug>/* — e.g. /mohap/brief.
 *   2. Query parameter: ?edition=<slug>. Used by admin tooling and
 *      automated tests. Sticky via localStorage so reloads preserve
 *      the override.
 *   3. localStorage flag: n71-edition. Set by (2) and respected on
 *      subsequent loads until cleared.
 *
 * Default — when none match — is the standard N71 product.
 */

export type Edition = "n71" | "ministry-moh" | "individual-elias";

interface EditionConfig {
  edition: Edition;
  workspaceSlug: string;
  isWhiteLabel: boolean;
}

const KNOWN_TENANTS: Record<string, EditionConfig> = {
  mohap: {
    edition: "ministry-moh",
    workspaceSlug: "mohap",
    isWhiteLabel: true,
  },
  elias: {
    // Elias edition — single-user, frontier-by-default, document-grounded.
    // Lives at /elias path-prefix. Backed by the n71-elias-worker Fly app
    // (fly.elias.toml) for traffic isolation; behaviour is selected at
    // request time from workspace.settings.edition === "individual-elias".
    edition: "individual-elias",
    workspaceSlug: "elias",
    isWhiteLabel: true,
  },
  // Add more tenant slugs here as the white-label catalog grows.
};

const STORAGE_KEY = "n71-edition";

function readQueryParamSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get("edition");
    if (slug && KNOWN_TENANTS[slug]) return slug;
  } catch {
    /* defensive: SSR or malformed search string */
  }
  return null;
}

function readLocalStorageSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const slug = window.localStorage.getItem(STORAGE_KEY);
    if (slug && KNOWN_TENANTS[slug]) return slug;
  } catch {
    /* localStorage may throw in strict iframes */
  }
  return null;
}

function readPathPrefixSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    // First non-empty path segment. /mohap, /mohap/, /mohap/brief all
    // resolve to "mohap". Trailing slashes and double slashes are
    // tolerated. Only registered tenant slugs are accepted — random
    // first segments fall through to default N71 routing.
    const path = window.location.pathname;
    const match = path.match(/^\/([a-z0-9-]+)(?:\/|$)/i);
    if (match && KNOWN_TENANTS[match[1].toLowerCase()]) {
      return match[1].toLowerCase();
    }
  } catch {
    /* defensive */
  }
  return null;
}

/**
 * Resolve the current edition. Path > query > localStorage > default.
 *
 * The query-param branch persists to localStorage so subsequent
 * navigation (which may strip the param) still resolves to the same
 * edition. The path branch is the source of truth in production —
 * URL alone tells you which tenant you're on.
 */
export function detectEdition(): EditionConfig {
  const pathSlug = readPathPrefixSlug();
  if (pathSlug) return KNOWN_TENANTS[pathSlug];

  const querySlug = readQueryParamSlug();
  if (querySlug) {
    try {
      window.localStorage.setItem(STORAGE_KEY, querySlug);
    } catch {
      /* ignore */
    }
    return KNOWN_TENANTS[querySlug];
  }

  const storedSlug = readLocalStorageSlug();
  if (storedSlug) return KNOWN_TENANTS[storedSlug];

  return { edition: "n71", workspaceSlug: "default", isWhiteLabel: false };
}

/** Drop any stored override. Used by an exit-hatch UI in admin tooling. */
export function clearEditionOverride(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export const KNOWN_EDITION_SLUGS = Object.keys(KNOWN_TENANTS);
